import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  cancelQrConnectMutation,
  changeChannelTokenMutation,
  connectByTokenMutation,
  deleteChannelMutation,
  disableChannelMutation,
  enableChannelMutation,
  getChannelCatalogQueryKey,
  getChannelCascadeQueryKey,
  listBotLinksQueryKey,
  listChannelEventsQueryKey,
  listChannelsQueryKey,
  refreshQrConnectMutation,
  relinkChannelMutation,
  saveChannelCascadeMutation,
  startQrConnectMutation,
  submitQrPasswordMutation,
  updateChannelMutation,
  type ChangeChannelTokenData,
  type ConnectByTokenData,
  type DeleteChannelData,
  type DisableChannelData,
  type EnableChannelData,
  type RefreshQrConnectData,
  type RelinkChannelData,
  type SaveChannelCascadeData,
  type StartQrConnectData,
  type UpdateChannelData,
} from '@/shared/api'
import { channelCascadeQuery, qrConnectQuery } from './queries'

export type SaveChannelCascadeRequest = SaveChannelCascadeData['body']
export type StartQrConnectRequest = StartQrConnectData['body']
export type RefreshQrConnectRequest = RefreshQrConnectData['path']
export type ConnectByTokenRequest = ConnectByTokenData['body']
export type DeleteChannelRequest = DeleteChannelData['path']
export type UpdateChannelRequest = Pick<UpdateChannelData, 'body' | 'path'>
export type RelinkChannelRequest = RelinkChannelData['path']
export type ChangeChannelTokenRequest = Pick<ChangeChannelTokenData, 'body' | 'path'>
export type EnableChannelRequest = EnableChannelData['path']
export type DisableChannelRequest = DisableChannelData['path']

function invalidateChannels(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: listChannelsQueryKey() })
  void queryClient.invalidateQueries({ queryKey: getChannelCatalogQueryKey() })
  void queryClient.invalidateQueries({ queryKey: listChannelEventsQueryKey() })
  void queryClient.invalidateQueries({ queryKey: getChannelCascadeQueryKey() })
  // Список ботов кешируется на пять минут — без этой строки поповер брони ещё столько же уверял
  // бы, что бот не подключён, хотя менеджер его только что подключил (или наоборот).
  void queryClient.invalidateQueries({ queryKey: listBotLinksQueryKey() })
}

/**
 * Ручная инвалидация всех запросов каналов — для случая вне мутации: QR-подключение завершается
 * ПОЛЛИНГОМ (getQrConnect → state: connected), а не мутацией, и список каналов надо перечитать
 * самим. Мутации подключения/действий инвалидируют сами через onSuccess.
 */
export function useInvalidateChannels() {
  const queryClient = useQueryClient()
  return useCallback(() => invalidateChannels(queryClient), [queryClient])
}

export function useSaveChannelCascade() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...saveChannelCascadeMutation(),
    onSuccess: (cascade) => {
      queryClient.setQueryData(channelCascadeQuery().queryKey, cascade)
      invalidateChannels(queryClient)
    },
  })

  return {
    ...mutation,
    mutate: (body: SaveChannelCascadeRequest) => mutation.mutate({ body }),
    mutateAsync: (body: SaveChannelCascadeRequest) => mutation.mutateAsync({ body }),
  }
}

/**
 * Сессия подключения: новое или продолжение незавершённого. Отказ говорит окно; ушло окно — тост
 * называет предмет, а исход проверяют списком каналов: незавершённый канал остаётся там.
 */
const CONNECT_META = {
  handledInPlace: true,
  subject: 'Подключение по QR-коду',
  unknownOutcome: 'проверьте список каналов: незавершённый канал удалите там.',
} as const

export function useStartQrConnect() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...startQrConnectMutation(),
    meta: CONNECT_META,
    onSuccess: (link) => {
      queryClient.setQueryData(qrConnectQuery(link.linkId).queryKey, link)
    },
  })

  return {
    ...mutation,
    mutate: (body: StartQrConnectRequest) => mutation.mutate({ body }),
    mutateAsync: (body: StartQrConnectRequest) => mutation.mutateAsync({ body }),
  }
}

export function useRefreshQrConnect() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...refreshQrConnectMutation(),
    // Отказ говорит окно; ушло окно — сессию оно уже отменило, и говорить не о чем.
    meta: { reportedByCaller: true },
    onSuccess: (link) => {
      queryClient.setQueryData(qrConnectQuery(link.linkId).queryKey, link)
    },
  })

  return {
    ...mutation,
    mutate: (path: RefreshQrConnectRequest) => mutation.mutate({ path }),
    mutateAsync: (path: RefreshQrConnectRequest) => mutation.mutateAsync({ path }),
  }
}

/**
 * Пароль аккаунта на шаге `password`. ⚠️ ЧУЖОЙ СЕКРЕТ: переменные мутации держат его, пока к ней
 * прицеплен наблюдатель, — вызывающий зовёт `reset()` после ответа, и `gcTime: 0` выбрасывает её
 * из кеша сразу, а не через пять минут. Отказ кладёт под поле вызывающий (`reportedByCaller`), а
 * ответ, пришедший после отмены привязки, говорить некому: окна и привязки уже нет.
 */
export function useSubmitQrPassword() {
  const mutation = useMutation({
    ...submitQrPasswordMutation(),
    gcTime: 0,
    meta: { reportedByCaller: true },
  })

  return {
    ...mutation,
    mutateAsync: ({ linkId, password }: { linkId: string; password: string }) =>
      mutation.mutateAsync({ path: { linkId }, body: { password } }),
  }
}

/**
 * Отмена привязки. Окно к этому мигу уже закрыто, показать отказ на месте некому — говорит
 * вызывающий (`reportedByCaller`) своим тостом: общий сказал бы «что-то сломалось» без предмета.
 */
export function useCancelQrConnect() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...cancelQrConnectMutation(),
    meta: { reportedByCaller: true },
    onSettled: () => invalidateChannels(queryClient),
  })

  return {
    ...mutation,
    mutate: (linkId: string) => mutation.mutate({ path: { linkId } }),
    mutateAsync: (linkId: string) => mutation.mutateAsync({ path: { linkId } }),
  }
}

export function useConnectByToken() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...connectByTokenMutation(),
    meta: { handledInPlace: true, subject: 'Подключение канала' },
    onSuccess: () => invalidateChannels(queryClient),
  })

  return {
    ...mutation,
    mutate: (body: ConnectByTokenRequest) => mutation.mutate({ body }),
    mutateAsync: (body: ConnectByTokenRequest) => mutation.mutateAsync({ body }),
  }
}

export function useDeleteChannel() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...deleteChannelMutation(),
    onSuccess: () => invalidateChannels(queryClient),
  })

  return {
    ...mutation,
    mutate: (path: DeleteChannelRequest) => mutation.mutate({ path }),
    mutateAsync: (path: DeleteChannelRequest) => mutation.mutateAsync({ path }),
  }
}

/** `subject` — у формы, которая говорит отказ сама; без него — общий тост (тумблер карточки). */
export function useUpdateChannel(subject?: string) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...updateChannelMutation(),
    ...(subject ? { meta: { handledInPlace: true, subject } } : {}),
    onSuccess: () => invalidateChannels(queryClient),
  })

  return {
    ...mutation,
    mutate: (request: UpdateChannelRequest) => mutation.mutate(request),
    mutateAsync: (request: UpdateChannelRequest) => mutation.mutateAsync(request),
  }
}

/**
 * Новая сессия у канала: повторный вход в подключённый (`relogin`) или продолжение незавершённого.
 * У повторного входа ушедшему окну говорить не о чем: канал остался как был, сессия истечёт сама.
 */
export function useRelinkChannel(relogin: boolean) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...relinkChannelMutation(),
    meta: relogin ? { reportedByCaller: true } : CONNECT_META,
    onSuccess: (link) => {
      queryClient.setQueryData(qrConnectQuery(link.linkId).queryKey, link)
    },
  })

  return {
    ...mutation,
    mutate: (path: RelinkChannelRequest) => mutation.mutate({ path }),
    mutateAsync: (path: RelinkChannelRequest) => mutation.mutateAsync({ path }),
  }
}

export function useChangeChannelToken() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...changeChannelTokenMutation(),
    meta: { handledInPlace: true, subject: 'Смена токена' },
    onSuccess: () => invalidateChannels(queryClient),
  })

  return {
    ...mutation,
    mutate: (request: ChangeChannelTokenRequest) => mutation.mutate(request),
    mutateAsync: (request: ChangeChannelTokenRequest) => mutation.mutateAsync(request),
  }
}

export function useEnableChannel() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...enableChannelMutation(),
    onSuccess: () => invalidateChannels(queryClient),
  })

  return {
    ...mutation,
    mutate: (path: EnableChannelRequest) => mutation.mutate({ path }),
    mutateAsync: (path: EnableChannelRequest) => mutation.mutateAsync({ path }),
  }
}

export function useDisableChannel() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...disableChannelMutation(),
    onSuccess: () => invalidateChannels(queryClient),
  })

  return {
    ...mutation,
    mutate: (path: DisableChannelRequest) => mutation.mutate({ path }),
    mutateAsync: (path: DisableChannelRequest) => mutation.mutateAsync({ path }),
  }
}
